(function () {
  'use strict';

  if (window.customElements && customElements.get('free-gift-progress')) return;

  /* One bar, up to three rewards, the same ladder the bracelet builder shows:
     - the free bracelet: claimed with the button (colour picked in a popup) and taken out
       again if the count drops below its threshold
     - the free mystery box: added by itself once its count is reached, taken out below it.
       If the shopper removes it by hand it stays out until the count drops and is earned
       again, as in the builder
     - the free watch: picked by the shopper in a popup, one at a time, taken out below its
       count
     Reward lines are told apart by their _free_gift value (yes / mystery / watch).
     On the bracelet builder page the builder keeps the mystery box and the watch in step
     itself, so this file only shows the progress there and never adds or removes them.

     The bracelet and the watch have to be chosen once they are earned. Reaching one on a
     page asks straight away, the cart page asks on arrival, the drawer asks while it is
     open, and checkout asks instead of going on.
     The popup for one still to choose cannot be dismissed; with one chosen it only changes
     it. Nothing is ever picked for the shopper, and a reward that is sold out or cannot be
     added never holds up checkout. */

  const SELECTORS = {
    bar: '[data-free-gift-bar]',
    status: '[data-free-gift-status]',
    hint: '[data-free-gift-hint]',
    count: '[data-free-gift-count]',
    claim: '[data-free-gift-claim]',
    claimLabel: '[data-free-gift-claim-label]',
    claimed: '[data-free-gift-claimed]',
    modal: '[data-free-gift-modal]',
    modalClose: '[data-free-gift-modal-close]',
    form: '[data-free-gift-form]',
    variantInput: '[data-free-gift-variant-input]',
    option: '[data-free-gift-option]',
    submit: '[data-free-gift-submit]',
    error: '[data-free-gift-error]',
    variantsScript: '[data-free-gift-variants]',
    media: '[data-free-gift-media]',
    cartItem: '[data-item][data-product-id]',
    tier: '[data-free-gift-tier]',
    watchOpen: '[data-free-gift-watch-open]',
    watchModal: '[data-free-gift-watch-modal]',
    watchPick: '[data-free-gift-pick-watch]',
    watchError: '[data-free-gift-watch-error]',
    change: '[data-free-gift-change]',
    builder: '[data-cbb]',
  };

  const CLASSES = {
    unlocked: 'is-unlocked',
    claimed: 'is-claimed',
    reached: 'is-reached',
    modalOpen: 'is-open',
    bodyLocked: 'free-gift-modal-open',
  };

  const CHANGE_URL = (window.theme && window.theme.routes && window.theme.routes.cart_change_url) || '/cart/change.js';
  const ADD_URL = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';

  // a mystery box the shopper took out stays out for the rest of the visit
  const DECLINED_KEY = 'charmsMysteryDeclined';
  const storage = {
    get(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { if (value) window.sessionStorage.setItem(key, value); else window.sessionStorage.removeItem(key); } catch (e) {} },
  };

  function changeLine(key, quantity) {
    return fetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then((response) => {
      if (!response.ok) throw new Error('cart change ' + response.status);
      return response.json();
    });
  }

  function addReward(variantId, kind) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('properties[_free_gift]', kind);
    return fetch(ADD_URL, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: formData,
    }).then((response) => response.json().then((data) => {
      if (!response.ok) throw new Error((data && (data.description || data.message)) || 'cart add ' + response.status);
      return data;
    }));
  }

  /* Every checkout button first puts the cart right from a fresh copy of it (one of each
     reward, none without its charms), so checkout never has to turn the order away. A
     reward still to be chosen opens its popup instead, and checkout waits. If the cart
     cannot be read, checkout goes ahead anyway, so it is never stuck. One bar is on any
     page (the cart page has no drawer). */
  function installCheckoutGate() {
    if (window.__freeGiftGate) return;
    window.__freeGiftGate = true;

    // the bracelet builder puts the cart right the same way before its own checkout button
    window.__freeGiftTidy = () => {
      const bar = document.querySelector('free-gift-progress');
      return bar && typeof bar.tidyForCheckout === 'function' ? bar.tidyForCheckout({ prompt: false }) : Promise.resolve(false);
    };

    let busy = false;
    let pass = false;

    const proceed = (target) => {
      pass = true;
      setTimeout(() => { pass = false; }, 4000);
      const form = target && target.form;
      if (form && target.name === 'checkout' && typeof form.requestSubmit === 'function') {
        form.requestSubmit(target);
        return;
      }
      const href = target && target.getAttribute && target.getAttribute('href');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = href || root + 'checkout';
    };

    const hold = (event, target) => {
      if (pass) return;
      const bar = document.querySelector('free-gift-progress');
      if (!bar || typeof bar.tidyForCheckout !== 'function') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      busy = true;
      if (target && target.classList) target.classList.add('is-loading');
      const done = () => {
        busy = false;
        if (target && target.classList) target.classList.remove('is-loading');
      };
      // on a bracelet builder page in its Watch tab, the builder first puts the watch
      // being built into the cart, so a checkout from the drawer includes it too
      const before = typeof window.__cbbBeforeCheckout === 'function' ? window.__cbbBeforeCheckout() : null;
      Promise.resolve(before).catch(() => null)
        .then(() => bar.tidyForCheckout())
        .then((waits) => { done(); if (!waits) proceed(target); })
        .catch((err) => {
          done();
          console.error('[free-gift] Could not check the cart before checkout', err);
          proceed(target);
        });
    };

    document.addEventListener('click', (event) => {
      const target = event.target && event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]');
      if (target) hold(event, target);
    }, true);
    document.addEventListener('submit', (event) => {
      if (event.submitter && event.submitter.name === 'checkout') hold(event, event.submitter);
    }, true);
  }

  class FreeGiftProgress extends HTMLElement {
    constructor() {
      super();

      this.threshold = parseInt(this.dataset.threshold, 10) || 10;
      this.qualifyingTag = (this.dataset.qualifyingTag || 'charms').toLowerCase();
      this.giftProductId = parseInt(this.dataset.giftProductId, 10) || 0;
      this.giftHasVariants = this.dataset.giftHasVariants === 'true';
      this.defaultVariantId = parseInt(this.dataset.giftDefaultVariantId, 10) || 0;
      this.progressTemplate = this.dataset.progressTemplate || "You're [count] [item] away from a Free Bracelet!";
      this.unlockedMessage = this.dataset.unlockedMessage || 'Congratulations! You unlocked a free bracelet.';
      this.claimLabel = this.dataset.claimLabel || 'Claim Bracelet';
      this.claimedLabel = this.dataset.claimedLabel || 'Bracelet added to your cart';
      this.itemOne = this.dataset.itemOne || 'charm';
      this.itemOther = this.dataset.itemOther || 'charms';

      this.mysteryVariantId = parseInt(this.dataset.mysteryVariantId, 10) || 0;
      this.mysteryAt = this.mysteryVariantId ? parseInt(this.dataset.mysteryThreshold, 10) || 0 : 0;
      this.watchAt = parseInt(this.dataset.watchThreshold, 10) || 0;
      this.top = Math.max(this.threshold, this.mysteryAt, this.watchAt);
      this.progressMystery = this.dataset.progressMystery || "You're [count] [item] away from a free mystery box!";
      this.progressWatch = this.dataset.progressWatch || "You're [count] [item] away from a free watch!";
      this.watchUnlocked = this.dataset.watchUnlocked || "You've unlocked a free watch! Pick the one you want.";
      this.allUnlocked = this.dataset.allUnlocked || "You've unlocked every reward!";
      this.watchPickLabel = this.dataset.watchPickLabel || 'Pick your free watch';
      this.watchChangeLabel = this.dataset.watchChangeLabel || 'Change watch';

      // free shipping: from shipAt charms, or a cart subtotal of shipAmount (shop currency, cents)
      this.shipAt = parseInt(this.dataset.shipAt, 10) || 0;
      // charms for free shipping, without the mystery boxes the reward count includes
      this.shipCount = parseInt(this.dataset.shipCount, 10) || 0;
      this.shipAmount = parseInt(this.dataset.shipAmount, 10) || 0;
      this.shipLeft = this.dataset.shipLeft || "You're [count] [item] or [amount] away from free shipping";
      this.subtotal = parseInt(this.dataset.subtotal, 10) || 0;
      this.moneySample = this.dataset.moneySample || '';

      this.qualifyingCount = parseInt(this.dataset.qualifyingCount, 10) || 0;
      this.giftLineKey = this.dataset.giftLineKey || '';
      this.giftInCart = !!this.giftLineKey;
      this.braceletLines = [];
      this.mysteryLines = [];
      this.watchLines = [];
      // a watch built in the bracelet builder's Watch tab (a paid line marked _cbb_watch):
      // from the watch's charm count the free watch discount takes that watch to zero,
      // so no second, free watch is asked for while it is in the cart
      this.builtWatch = false;
      // a reward that could not be added stops being asked for on this page
      this.failed = { bracelet: false, watch: false };

      this.handleCartChange = this.handleCartChange.bind(this);
      this.handleDrawerOpen = this.handleDrawerOpen.bind(this);
      this.handleClaimClick = this.handleClaimClick.bind(this);
      this.handleModalClose = this.handleModalClose.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleOptionChange = this.handleOptionChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleWatchOpen = this.handleWatchOpen.bind(this);
    }

    connectedCallback() {
      this.barFill = this.querySelector(SELECTORS.bar);
      this.statusEl = this.querySelector(SELECTORS.status);
      this.hintEl = this.querySelector(SELECTORS.hint);
      this.countEl = this.querySelector(SELECTORS.count);
      this.claimBtn = this.querySelector(SELECTORS.claim);
      this.claimedEl = this.querySelector(SELECTORS.claimed);
      this.modal = this.querySelector(SELECTORS.modal);
      this.form = this.querySelector(SELECTORS.form);
      this.variantInput = this.querySelector(SELECTORS.variantInput);
      this.errorEl = this.querySelector(SELECTORS.error);
      this.submitBtn = this.querySelector(SELECTORS.submit);
      this.tierEls = Array.prototype.slice.call(this.querySelectorAll(SELECTORS.tier));
      this.watchBtn = this.querySelector(SELECTORS.watchOpen);
      this.watchModal = this.querySelector(SELECTORS.watchModal);
      this.changeBtn = this.querySelector(SELECTORS.change);

      const variantsScript = this.querySelector(SELECTORS.variantsScript);
      if (variantsScript) {
        try {
          this.variants = JSON.parse(variantsScript.textContent);
        } catch (err) {
          this.variants = [];
        }
      } else {
        this.variants = [];
      }

      // Teleport the modals to <body> so they escape the cart drawer's
      // stacking context (transforms / overflow break position: fixed).
      if (this.modal && this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal);
      }
      if (this.watchModal && this.watchModal.parentNode !== document.body) {
        document.body.appendChild(this.watchModal);
      }

      // Query media AFTER teleport (modal now lives on body, and children
      // are not available in the constructor).
      this.modalMedia = this.modal
        ? this.modal.querySelector(SELECTORS.media)
        : this.querySelector(SELECTORS.media);

      if (this.modal) {
        this.modal.addEventListener('click', (event) => {
          if (event.target.matches(SELECTORS.modalClose)) {
            this.closeModal();
          }
        });

        this.modal.querySelectorAll(SELECTORS.option).forEach((input) => {
          input.addEventListener('change', this.handleOptionChange);
        });
      }

      if (this.watchModal) {
        this.watchError = this.watchModal.querySelector(SELECTORS.watchError);
        this.watchModal.addEventListener('click', (event) => {
          if (event.target.closest(SELECTORS.modalClose)) {
            this.closeWatchModal();
            return;
          }
          const pick = event.target.closest(SELECTORS.watchPick);
          if (pick) this.pickWatch(pick);
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleFormSubmit);
        this.updateVariantFromOptions();
      }

      if (this.claimBtn) {
        this.claimBtn.addEventListener('click', this.handleClaimClick);
      }

      if (this.watchBtn) {
        this.watchBtn.addEventListener('click', this.handleWatchOpen);
      }

      if (this.changeBtn) {
        this.changeBtn.addEventListener('click', (event) => {
          event.preventDefault();
          this.openModal();
        });
      }

      document.addEventListener('theme:cart:change', this.handleCartChange);
      document.addEventListener('theme:product:added', this.handleCartChange);
      document.addEventListener('theme:cart-drawer:open', this.handleDrawerOpen);
      document.addEventListener('keydown', this.handleKeydown);
      installCheckoutGate();

      this.recomputeFromCartItems();
      this.render();

      // Reconcile any inconsistent state from a previous session.
      this.maybeAutoRemoveGift();
      this.syncRewards();

      if (this.canAsk()) this.promptDue();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.handleCartChange);
      document.removeEventListener('theme:product:added', this.handleCartChange);
      document.removeEventListener('theme:cart-drawer:open', this.handleDrawerOpen);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    handleCartChange() {
      const before = this.qualifyingCount;
      this.recomputeFromCartItems();
      this.render();
      this.maybeAutoRemoveGift();
      this.syncRewards();
      if (this.canAsk() || this.justReached(before)) this.promptDue();
    }

    /**
     * A charm added on this page that reaches a reward asks for it there and then, since
     * adding a charm does not open the drawer everywhere. The builder asks by itself.
     */
    justReached(before) {
      if (document.querySelector(SELECTORS.builder)) return false;
      const now = this.qualifyingCount;
      return [this.threshold, this.watchAt].some((at) => at > 0 && before < at && now >= at);
    }

    handleDrawerOpen() {
      this.promptDue();
    }

    /**
     * The cart page asks as soon as a reward is due; the drawer only while it is open,
     * so arriving on a page with a full cart never interrupts browsing.
     */
    canAsk() {
      const drawer = this.closest('cart-drawer');
      return !drawer || drawer.classList.contains('is-open');
    }

    braceletDue() {
      if (this.failed.bracelet || this.giftInCart || this.qualifyingCount < this.threshold) return false;
      // every colour sold out leaves nothing to choose
      if (!this.variants || !this.variants.length) return !!this.defaultVariantId;
      return this.variants.some((variant) => variant.available);
    }

    watchDue() {
      if (this.builtWatch) return false;
      if (this.failed.watch || !this.watchAt || this.qualifyingCount < this.watchAt || this.watchLines.length) return false;
      // the list only holds watches in stock
      return !!(this.watchModal && this.watchModal.querySelector(SELECTORS.watchPick));
    }

    /**
     * Put the cart right just before checkout, from a fresh copy of it rather than what
     * this bar last saw (another tab may have changed it): one free bracelet, mystery box
     * and watch, each of quantity one and only with the charms it needs, the newest one
     * kept, and no free reward product that came in without the theme. Charms are read
     * from the cart's own line items, which carry the qualifying tag. Resolves true when
     * a reward is still to be chosen: its popup is open then and checkout should wait.
     */
    tidyForCheckout(options = {}) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || root + 'cart';
      return Promise.all([
        fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((response) => response.json()),
        fetch(cartUrl + '?section_id=api-cart-items', { credentials: 'same-origin' }).then((response) => response.text()),
      ]).then(([cart, html]) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const qualifying = {};
        doc.querySelectorAll('[data-line-key]').forEach((el) => {
          qualifying[el.dataset.lineKey] = el.dataset.qualifyingItem === 'true';
        });

        let charms = 0;
        let built = false;
        const rewards = { bracelet: [], mystery: [], watch: [] };
        const strays = [];
        (cart.items || []).forEach((item) => {
          const gift = String((item.properties && item.properties._free_gift) || '').trim().toLowerCase();
          if (item.properties && item.properties._cbb_watch) built = true;
          if (gift) {
            rewards[gift === 'mystery' || gift === 'watch' ? gift : 'bracelet'].push(item);
          } else if ((this.giftProductId && item.product_id === this.giftProductId) || (this.mysteryVariantId && item.variant_id === this.mysteryVariantId)) {
            strays.push(item);
          } else if (qualifying[item.key]) {
            charms += item.quantity;
          }
        });

        const steps = [];
        strays.forEach((item) => steps.push(() => changeLine(item.key, 0)));
        // the newest line comes first in the cart, and that is the one kept
        const keep = (lines, needs) => {
          if (!lines.length) return null;
          if (needs > 0 && charms < needs) {
            lines.forEach((item) => steps.push(() => changeLine(item.key, 0)));
            return null;
          }
          lines.forEach((item, i) => {
            if (i > 0) steps.push(() => changeLine(item.key, 0));
            else if (item.quantity > 1) steps.push(() => changeLine(item.key, 1));
          });
          return lines[0];
        };
        const bracelet = keep(rewards.bracelet, this.threshold);
        keep(rewards.mystery, this.mysteryAt);
        // with a watch built in the Watch tab, that watch is the one the discount makes
        // free, so a free watch picked earlier goes
        let watch = null;
        if (built) rewards.watch.forEach((item) => steps.push(() => changeLine(item.key, 0)));
        else watch = keep(rewards.watch, this.watchAt);

        return steps.reduce((chain, step) => chain.then(step), Promise.resolve()).then(() => {
          if (steps.length) this.requestCartRefresh();
          this.qualifyingCount = charms;
          this.giftLineKey = bracelet ? bracelet.key : '';
          this.giftInCart = !!bracelet;
          this.builtWatch = built;
          this.watchLines = watch ? [{ key: watch.key, qty: 1, productId: watch.product_id }] : [];
          if (options.prompt === false || !this.needsChoice()) return false;
          this.promptDue(true);
          return true;
        });
      });
    }

    needsChoice() {
      return this.braceletDue() || this.watchDue();
    }

    /**
     * Ask for the reward still to choose: the bracelet first, then the watch.
     */
    promptDue(fromCheckout) {
      if (this.isOpen(this.modal) || this.isOpen(this.watchModal)) return;
      if (this.braceletDue()) {
        // the builder asks for the bracelet by itself; the cart only steps in at checkout
        if (!fromCheckout && document.querySelector(SELECTORS.builder)) return;
        if (this.modal) this.openModal();
        else this.addGiftToCart(this.defaultVariantId);
      } else if (this.watchDue()) {
        this.openWatchModal();
      }
    }

    isOpen(modal) {
      return !!modal && modal.classList.contains(CLASSES.modalOpen);
    }

    // a popup for a reward still to choose has no close button and ignores the backdrop
    setRequired(modal, required) {
      if (!modal) return;
      modal.classList.toggle('is-required', required);
      modal.querySelectorAll('.free-gift-modal__close').forEach((button) => {
        button.style.display = required ? 'none' : '';
      });
    }

    /**
     * Read the rendered cart items to figure out qualifying count and the reward lines.
     * A line can be rendered twice on one page (the cart page and the drawer), so each
     * line key is counted once.
     */
    recomputeFromCartItems() {
      const items = document.querySelectorAll(SELECTORS.cartItem);
      const seen = {};
      let qualifying = 0;
      const bracelet = [];
      const mystery = [];
      const watch = [];
      let built = false;
      let subtotal = 0;
      let shipQualifying = 0;
      let priced = false;

      items.forEach((item) => {
        const key = item.dataset.lineKey || '';
        if (key) {
          if (seen[key]) return;
          seen[key] = true;
        }

        const isQualifying = item.dataset.qualifyingItem === 'true';
        const isFreeGift = item.dataset.freeGiftLine === 'true';
        const kind = (item.dataset.freeGiftKind || '').toLowerCase();
        const qty = parseInt(item.dataset.lineQuantity, 10) || 0;
        const productId = parseInt(item.dataset.productId, 10) || 0;
        if (item.dataset.linePrice !== undefined) {
          priced = true;
          subtotal += parseInt(item.dataset.linePrice, 10) || 0;
        }
        if (item.dataset.builtWatch === 'true') built = true;

        if (isFreeGift) {
          if (kind === 'mystery') {
            mystery.push({ key: key, qty: qty });
          } else if (kind === 'watch') {
            watch.push({ key: key, qty: qty, productId: productId });
          } else if (!this.giftProductId || productId === this.giftProductId) {
            bracelet.push({ key: key, qty: qty });
          }
        } else if (isQualifying) {
          qualifying += qty;
          if (item.dataset.mysteryBox !== 'true') shipQualifying += qty;
        }
      });

      this.qualifyingCount = qualifying;
      if (priced) this.subtotal = subtotal;
      this.shipCount = shipQualifying;
      // the newest line comes first in the cart, and that is the bracelet that is kept
      this.braceletLines = bracelet;
      this.giftLineKey = bracelet.length ? bracelet[0].key : '';
      this.giftInCart = !!this.giftLineKey;
      this.mysteryLines = mystery;
      this.watchLines = watch;
      this.builtWatch = built;

      // A mystery box that disappears while it is still earned was taken out by the
      // shopper (this file only ever removes it below its count).
      const last = window.__freeGiftLast;
      if (last && last.mystery && !mystery.length && this.mysteryAt && qualifying >= this.mysteryAt) {
        storage.set(DECLINED_KEY, '1');
      }
      if (this.mysteryAt && qualifying < this.mysteryAt) {
        storage.set(DECLINED_KEY, '');
      }
      window.__freeGiftLast = { mystery: mystery.length > 0 };
    }

    fillCount(template, count) {
      const word = count === 1 ? this.itemOne : this.itemOther;
      return template.replace('[count]', count).replace('[item]', word);
    }

    /** A money amount in the cart's currency, written the way the shop writes it. */
    formatMoney(cents) {
      const value = Math.max(0, cents) / 100;
      const match = this.moneySample.match(/0([.,])01/);
      if (match) return this.moneySample.replace(match[0], value.toFixed(2).replace('.', match[1]));
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: currency }).format(value);
      } catch (err) {
        return value.toFixed(2);
      }
    }

    /** The cart subtotal that makes shipping free, in the cart's currency (cents). */
    shipNeed() {
      const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      return this.shipAmount ? Math.round(this.shipAmount * rate) : 0;
    }

    /**
     * Free from shipAt charms or from the cart subtotal, the rule the checkout applies.
     * The subtotal is in the shopper's currency, so the amount is converted at the
     * storefront's rate.
     */
    shipFree() {
      const need = this.shipNeed();
      return (this.shipAt > 0 && this.shipCount >= this.shipAt) || (need > 0 && this.subtotal >= need);
    }

    statusMessage() {
      const n = this.qualifyingCount;
      // free shipping is the first step, so it is asked for before the bracelet
      if (this.shipAt > 0 && !this.shipFree()) {
        const message = this.fillCount(this.shipLeft, Math.max(0, this.shipAt - this.shipCount));
        return message.replace('[amount]', this.formatMoney(this.shipNeed() - this.subtotal));
      }
      if (n < this.threshold) return this.fillCount(this.progressTemplate, this.threshold - n);
      if (!this.giftInCart) return this.unlockedMessage;

      // the next reward still to reach
      const next = [this.mysteryAt, this.watchAt].filter((at) => at > n).sort((a, b) => a - b)[0];
      if (next) {
        return this.fillCount(next === this.mysteryAt ? this.progressMystery : this.progressWatch, next - n);
      }
      if (this.watchAt && !this.watchLines.length && !this.builtWatch) return this.watchUnlocked;
      if (this.mysteryAt || this.watchAt) return this.allUnlocked;
      return this.unlockedMessage;
    }

    render() {
      const unlocked = this.qualifyingCount >= this.threshold;
      const displayCount = Math.min(this.qualifyingCount, this.top);
      const percent = Math.min(100, (this.qualifyingCount / this.top) * 100);

      this.classList.toggle(CLASSES.unlocked, unlocked);
      this.classList.toggle(CLASSES.claimed, this.giftInCart);

      if (this.barFill) {
        this.barFill.style.setProperty('--free-gift-progress', percent + '%');
      }

      this.setAttribute('aria-valuenow', this.qualifyingCount);

      // the free shipping milestone is also reached by the cart total
      const shipFree = this.shipFree();
      this.tierEls.forEach((el) => {
        const reached = el.dataset.freeGiftTierKind === 'shipping'
          ? shipFree
          : this.qualifyingCount >= (parseInt(el.dataset.freeGiftTier, 10) || 0);
        el.classList.toggle(CLASSES.reached, reached);
      });

      if (this.countEl) {
        this.countEl.textContent = displayCount;
      }

      if (this.hintEl) {
        this.hintEl.innerHTML =
          '<span data-free-gift-count>' +
          displayCount +
          '</span> / ' +
          this.top +
          ' ' +
          this.itemOther;
      }

      if (this.statusEl) {
        this.statusEl.textContent = this.statusMessage();
      }

      this.renderClaimUI(unlocked);
      this.renderWatchUI();
    }

    renderWatchUI() {
      if (!this.watchBtn) return;
      const earned = this.watchAt > 0 && this.qualifyingCount >= this.watchAt;
      const has = this.watchLines.length > 0;
      // the watch built in the Watch tab is changed there, on its dial
      this.watchBtn.hidden = !earned || this.builtWatch;
      this.watchBtn.textContent = has ? this.watchChangeLabel : this.watchPickLabel;
      this.watchBtn.classList.toggle('free-gift__watch--change', has);
      if (!earned) this.closeWatchModal();
    }

    renderClaimUI(unlocked) {
      if (unlocked && !this.claimBtn) {
        this.claimBtn = document.createElement('button');
        this.claimBtn.type = 'button';
        this.claimBtn.className = 'btn btn--primary btn--solid free-gift__claim';
        this.claimBtn.setAttribute('data-free-gift-claim', '');
        const label = document.createElement('span');
        label.setAttribute('data-free-gift-claim-label', '');
        label.textContent = this.claimLabel;
        this.claimBtn.appendChild(label);
        this.claimBtn.addEventListener('click', this.handleClaimClick);

        const panel = this.querySelector('.free-gift__panel');
        // ahead of the watch button, where the server-rendered one sits
        if (panel) panel.insertBefore(this.claimBtn, this.watchBtn || null);
      }

      if (this.claimBtn) {
        if (unlocked && !this.giftInCart) {
          this.claimBtn.hidden = false;
          this.claimBtn.disabled = false;
        } else {
          this.claimBtn.hidden = true;
          this.claimBtn.disabled = true;
        }
      }

      if (!this.claimedEl && unlocked) {
        this.claimedEl = document.createElement('p');
        this.claimedEl.className = 'free-gift__claimed';
        this.claimedEl.setAttribute('data-free-gift-claimed', '');
        this.claimedEl.innerHTML = '<span>' + this.claimedLabel + '</span>';
        const panel = this.querySelector('.free-gift__panel');
        if (panel) panel.insertBefore(this.claimedEl, this.watchBtn || null);
      }

      if (this.claimedEl) {
        if (this.giftInCart && this._giftJustClaimed) {

          this.claimedEl.hidden = false;
          this.claimedEl.style.transition = 'opacity 0.6s ease';
          this.claimedEl.style.opacity = '1';

          clearTimeout(this._claimedFadeTimer);
          this._claimedFadeTimer = setTimeout(() => {
            this._giftJustClaimed = false;  // reset HERE, after fade starts
            this.claimedEl.style.opacity = '0';
            setTimeout(() => {
              this.claimedEl.hidden = true;
              this.claimedEl.style.opacity = '1';
            }, 600);
          }, 2000);

        } else if (!this._giftJustClaimed) {
          this.claimedEl.hidden = true;
        }
      }

      if (this.changeBtn) {
        this.changeBtn.hidden = !(unlocked && this.giftInCart);
      }
    }

    singularTagWord() {
      if (this.qualifyingTag.endsWith('s')) {
        return this.qualifyingTag.slice(0, -1);
      }
      return this.qualifyingTag;
    }

    handleClaimClick(event) {
      event.preventDefault();
      if (this.giftInCart) return;

      if (this.giftHasVariants) {
        this.openModal();
      } else {
        this.addGiftToCart(this.defaultVariantId);
      }
    }

    /**
     * On the bracelet builder page the builder has its own colour popup, so the cart does
     * not open a second one there: two popups were how a bracelet got added twice. The
     * drawer closes and the builder's popup opens. If the builder cannot open one, the
     * cart's own popup is used, so checkout is never stuck.
     */
    askBuilder() {
      if (!document.querySelector(SELECTORS.builder)) return false;
      const ask = { handled: false };
      document.dispatchEvent(new CustomEvent('cbb:open-gift', { detail: ask }));
      if (!ask.handled) return false;
      const drawer = this.closest('cart-drawer');
      const close = drawer && drawer.querySelector('[data-cart-drawer-close]');
      if (close && drawer.classList.contains('is-open')) close.click();
      return true;
    }

    openModal() {
      if (!this.modal) return;
      if (this.askBuilder()) return;
      const required = this.braceletDue();
      this.setRequired(this.modal, required);
      this.modal.hidden = false;
      this.modal.classList.add(CLASSES.modalOpen);
      document.body.classList.add(CLASSES.bodyLocked);

      // changing the colour starts from the one in the cart
      const current = this.giftInCart && this.variants.find((variant) => String(variant.id) === this.giftVariantId());
      if (current) {
        this.modal.querySelectorAll('[data-free-gift-option-index]').forEach((fieldset) => {
          const value = current['option' + (parseInt(fieldset.dataset.freeGiftOptionIndex, 10) + 1)];
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = input.value === value;
          });
        });
      }

      // Make sure the image matches the currently selected swatch on open.
      this.updateVariantFromOptions();

      // Focus the close button for keyboard accessibility, or the claim button when the
      // choice has to be made
      const focusBtn = this.modal.querySelector(required ? SELECTORS.submit : '.free-gift-modal__close');
      if (focusBtn) focusBtn.focus();
    }

    closeModal(force) {
      if (!this.modal) return;
      if (force !== true && this.braceletDue()) return;
      this.modal.classList.remove(CLASSES.modalOpen);
      document.body.classList.remove(CLASSES.bodyLocked);

      // Hide after the animation
      setTimeout(() => {
        if (!this.modal.classList.contains(CLASSES.modalOpen)) {
          this.modal.hidden = true;
        }
      }, 250);
    }

    handleModalClose(event) {
      event.preventDefault();
      this.closeModal();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.modal && this.modal.classList.contains(CLASSES.modalOpen)) {
        this.closeModal();
      }
      if (this.watchModal && this.watchModal.classList.contains(CLASSES.modalOpen)) {
        this.closeWatchModal();
      }
    }

    handleOptionChange() {
      this.updateVariantFromOptions();
    }

    /**
     * Match selected option values to the right variant id and swap the image.
     */
    updateVariantFromOptions() {
      if (!this.variants || !this.variants.length || !this.modal) return;

      const fieldsets = this.modal.querySelectorAll('[data-free-gift-option-index]');
      if (!fieldsets.length) return;

      const selectedOptions = [];
      fieldsets.forEach((fieldset) => {
        const idx = parseInt(fieldset.dataset.freeGiftOptionIndex, 10);
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        selectedOptions[idx] = checked ? checked.value : null;
      });

      const match = this.variants.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];
        return selectedOptions.every((value, idx) => {
          if (value === null || value === undefined) return true;
          return variantOptions[idx] === value;
        });
      });

      if (match && this.variantInput) {
        this.variantInput.value = match.id;
        if (this.submitBtn) {
          this.submitBtn.disabled = !match.available;
          this.submitBtn.textContent = match.available
            ? this.claimLabel
            : ((window.theme && window.theme.strings && window.theme.strings.soldOut) || 'Sold out');
        }
        this.swapModalImage(match);
      }
    }

    /**
     * Resolve a variant's image src from either featured_image or featured_media.
     */
    variantImageSrc(variant) {
      if (variant.featured_image && variant.featured_image.src) {
        return { src: variant.featured_image.src, alt: variant.featured_image.alt };
      }
      if (
        variant.featured_media &&
        variant.featured_media.preview_image &&
        variant.featured_media.preview_image.src
      ) {
        return { src: variant.featured_media.preview_image.src, alt: variant.featured_media.alt };
      }
      return null;
    }

    /**
     * Update the modal's main image to the matched variant's image.
     */
    swapModalImage(variant) {
      if (!this.modalMedia || !variant) return;

      const resolved = this.variantImageSrc(variant);
      if (!resolved) return;

      let src = resolved.src;
      try {
        const u = new URL(src, window.location.href);
        u.searchParams.set('width', '600');
        src = u.toString();
      } catch (e) {}

      let img = this.modalMedia.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.className = 'free-gift-modal__image';
        img.loading = 'lazy';
        this.modalMedia.appendChild(img);
      }

      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
      if (resolved.alt) img.alt = resolved.alt;
    }

    handleFormSubmit(event) {
      event.preventDefault();
      if (!this.form) return;

      const variantId = this.variantInput ? this.variantInput.value : this.defaultVariantId;
      if (!variantId) return;

      // changing the colour: the bracelet in the cart makes way for the new one
      if (this.giftInCart && this.giftLineKey) {
        if (this.giftVariantId() === String(variantId)) {
          this.closeModal();
          return;
        }
        this.addGiftToCart(variantId, this.giftLineKey);
        return;
      }

      this.addGiftToCart(variantId);
    }

    // a cart line key starts with its variant id
    giftVariantId() {
      return String(this.giftLineKey || '').split(':')[0];
    }

    addGiftToCart(variantId, replaceKey) {
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.classList.add('is-loading');
      }
      if (this.claimBtn) {
        this.claimBtn.disabled = true;
        this.claimBtn.classList.add('is-loading');
      }
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('properties[_free_gift]', 'yes');

      (replaceKey ? changeLine(replaceKey, 0) : Promise.resolve())
        .then(() => fetch(ADD_URL, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: formData,
        }))
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            throw new Error((data && (data.description || data.message)) || ((window.theme && window.theme.strings && window.theme.strings.giftAddError) || 'Unable to add gift.'));
          }

          this.giftInCart = true;
          this._giftJustClaimed = true;
          if (data && data.key) {
            this.giftLineKey = data.key;
          }

          this.closeModal();
          this.requestCartRefresh();
        })
        .catch((err) => {
          if (this.errorEl) {
            this.errorEl.hidden = false;
            this.errorEl.textContent = err.message || ((window.theme && window.theme.strings && window.theme.strings.giftAddError) || 'Could not add free gift.');
          }
          // a bracelet that cannot be added must not hold up checkout
          this.failed.bracelet = true;
          this.setRequired(this.modal, false);
          // a colour change may have taken the old one out already
          if (replaceKey) this.requestCartRefresh();
        })
        .finally(() => {
          if (this.submitBtn) {
            this.submitBtn.disabled = false;
            this.submitBtn.classList.remove('is-loading');
          }
          if (this.claimBtn) {
            this.claimBtn.disabled = false;
            this.claimBtn.classList.remove('is-loading');
          }
        });
    }

    /**
     * If the gift sits in the cart but qualifying items dropped below threshold,
     * remove the gift line automatically.
     */
    maybeAutoRemoveGift() {
      if (!this.giftInCart || !this.giftLineKey) return;
      if (this.qualifyingCount >= this.threshold) return;

      this.removeGiftLine(this.giftLineKey);
    }

    removeGiftLine(key) {
      const removingKey = key || this.giftLineKey;
      if (!removingKey) return;

      const previousKey = this.giftLineKey;
      this.giftLineKey = '';
      this.giftInCart = false;

      fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: removingKey, quantity: 0 }),
      })
        .then((response) => response.json())
        .then(() => {
          this.requestCartRefresh();
        })
        .catch((err) => {
          // Restore state on failure to avoid getting out of sync.
          this.giftLineKey = previousKey;
          this.giftInCart = !!previousKey;
          console.error('[free-gift] Failed to auto-remove gift', err);
        });
    }

    /**
     * Keep the rewards in step with the count: one free bracelet of quantity one, the
     * mystery box in while it is earned (unless the shopper took it out), at most one
     * watch, and neither below its count (a bracelet below its count is taken out by
     * maybeAutoRemoveGift). Only the first bar on the page does this, and never on
     * the builder page, so two scripts never add the same reward twice.
     */
    syncRewards() {
      if (document.querySelector(SELECTORS.builder)) return;
      if (document.querySelector('free-gift-progress') !== this) return;
      if (window.__freeGiftRewardSync) return;

      const n = this.qualifyingCount;
      const steps = [];

      // a second add (a double tap, two tabs) merges into the same line or makes another one
      if (n >= this.threshold) {
        this.braceletLines.forEach((line, i) => {
          if (i > 0) steps.push(() => changeLine(line.key, 0));
          else if (line.qty > 1) steps.push(() => changeLine(line.key, 1));
        });
      }

      if (this.mysteryAt) {
        if (n < this.mysteryAt) {
          this.mysteryLines.forEach((line) => steps.push(() => changeLine(line.key, 0)));
        } else if (!this.mysteryLines.length) {
          const failedAt = window.__freeGiftMysteryFailed || 0;
          if (storage.get(DECLINED_KEY) !== '1' && Date.now() - failedAt > 60000) {
            steps.push(() => addReward(this.mysteryVariantId, 'mystery').catch((err) => {
              window.__freeGiftMysteryFailed = Date.now();
              throw err;
            }));
          }
        } else {
          this.mysteryLines.forEach((line, i) => {
            if (i > 0) steps.push(() => changeLine(line.key, 0));
            else if (line.qty > 1) steps.push(() => changeLine(line.key, 1));
          });
        }
      }

      if (this.watchAt) {
        this.watchLines.forEach((line, i) => {
          if (n < this.watchAt || i > 0) steps.push(() => changeLine(line.key, 0));
          else if (line.qty > 1) steps.push(() => changeLine(line.key, 1));
        });
      }

      if (!steps.length) return;

      window.__freeGiftRewardSync = true;
      steps
        .reduce((chain, step) => chain.then(step), Promise.resolve())
        .catch((err) => console.error('[free-gift] Could not update the rewards', err))
        .then(() => {
          window.__freeGiftRewardSync = false;
          this.requestCartRefresh();
        });
    }

    handleWatchOpen(event) {
      event.preventDefault();
      this.openWatchModal();
    }

    openWatchModal() {
      if (!this.watchModal) return;
      const current = this.watchLines[0];
      this.watchModal.querySelectorAll(SELECTORS.watchPick).forEach((button) => {
        button.classList.toggle('is-on', !!current && (parseInt(button.dataset.productId, 10) || 0) === current.productId);
        button.disabled = false;
      });
      if (this.watchError) {
        this.watchError.hidden = true;
        this.watchError.textContent = '';
      }
      const required = this.watchDue();
      this.setRequired(this.watchModal, required);
      this.watchModal.hidden = false;
      this.watchModal.classList.add(CLASSES.modalOpen);
      document.body.classList.add(CLASSES.bodyLocked);
      const focusBtn = this.watchModal.querySelector(required ? SELECTORS.watchPick : '.free-gift-modal__close');
      if (focusBtn) focusBtn.focus();
    }

    closeWatchModal(force) {
      if (!this.watchModal || !this.watchModal.classList.contains(CLASSES.modalOpen)) return;
      if (force !== true && this.watchDue()) return;
      this.watchModal.classList.remove(CLASSES.modalOpen);
      document.body.classList.remove(CLASSES.bodyLocked);
      setTimeout(() => {
        if (!this.watchModal.classList.contains(CLASSES.modalOpen)) {
          this.watchModal.hidden = true;
        }
      }, 250);
    }

    /**
     * One watch at a time: picking another replaces the one already in the cart.
     */
    pickWatch(button) {
      const variantId = parseInt(button.dataset.freeGiftPickWatch, 10) || 0;
      const productId = parseInt(button.dataset.productId, 10) || 0;
      if (!variantId) return;

      if (this.watchLines.some((line) => line.productId === productId)) {
        this.closeWatchModal();
        return;
      }

      const buttons = this.watchModal.querySelectorAll(SELECTORS.watchPick);
      buttons.forEach((b) => { b.disabled = true; });
      button.classList.add('is-loading');

      this.watchLines
        .reduce((chain, line) => chain.then(() => changeLine(line.key, 0)), Promise.resolve())
        .then(() => addReward(variantId, 'watch'))
        .then(() => {
          this.closeWatchModal(true);
          this.requestCartRefresh();
        })
        .catch((err) => {
          if (this.watchError) {
            this.watchError.hidden = false;
            this.watchError.textContent = err.message || 'Could not add the watch.';
          }
          // a watch that cannot be added must not hold up checkout
          this.failed.watch = true;
          this.setRequired(this.watchModal, false);
          this.requestCartRefresh();
        })
        .finally(() => {
          buttons.forEach((b) => { b.disabled = false; });
          button.classList.remove('is-loading');
        });
    }

    /**
     * Trigger the existing cart re-render flow so the line items update.
     */
    requestCartRefresh() {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    }
  }

  customElements.define('free-gift-progress', FreeGiftProgress);
})();
